import {
  IntegrationKey,
  FamousIntegrationSettings,
  PlaidIntegrationSettings,
  PaymentsIntegrationSettings,
} from '@erp/rpc';
import {
  StorefrontIcon,
  PackageIcon,
  LightningIcon,
  BankIcon,
  CreditCardIcon,
  Icon,
} from '@phosphor-icons/react';

export interface IntegrationConfig {
  key: IntegrationKey;
  name: string;
  description: string;
  longDescription: string;
  icon: Icon;
  category: 'erp' | 'inventory' | 'accounting' | 'shipping' | 'other';
  features: string[];
  isPopular?: boolean;
  defaultSettings?: unknown; // Default settings for when connecting the integration
}

export const INTEGRATIONS_CONFIG = {
  [IntegrationKey.Famous]: {
    key: IntegrationKey.Famous,
    name: 'FAMOUS™',
    description: 'ERP integration for fresh produce distributors',
    longDescription:
      'FAMOUS™ is a comprehensive ERP solution designed specifically for fresh produce distributors. This integration enables seamless synchronization of products, customers, inventory, and transactions from FAMOUS™',
    icon: StorefrontIcon,
    category: 'erp',
    features: [
      'Product catalog synchronization',
      'Customer data management',
      'Real-time inventory updates',
      'Custom product mapping',
      'Automated data synchronization',
      'FAPI translation support',
    ],
    isPopular: false,
    defaultSettings: {
      productMapping: {
        useCustomerProducts: true,
      },
      salesOrders: {
        removeZeroQuantityLineItemsOnApproval: true,
      },
    } satisfies FamousIntegrationSettings,
  },
  [IntegrationKey.Plaid]: {
    key: IntegrationKey.Plaid,
    name: 'Plaid',
    description: 'Connect bank accounts for payments and reconciliation',
    longDescription:
      'Plaid enables secure bank account connections for initiating vendor payments and automating reconciliation. Connect multiple bank accounts to streamline your financial operations.',
    icon: BankIcon,
    category: 'accounting',
    features: [
      'Secure bank account authentication',
      'Pull transaction history',
      'Check account balances',
      'Support for multiple bank connections',
      'Bank-level security and encryption',
    ],
    isPopular: false,
    defaultSettings: {
      accounts: [],
    } satisfies PlaidIntegrationSettings,
  },
  [IntegrationKey.Payments]: {
    key: IntegrationKey.Payments,
    name: 'Payments',
    description: 'ACH payments via Checkbook.io',
    longDescription:
      'Enable ACH payments to vendors through Checkbook.io. Link your bank accounts from Plaid and verify them via micro-deposits to start making secure payments.',
    icon: CreditCardIcon,
    category: 'accounting',
    features: [
      'ACH payments to vendors',
      'Micro-deposit bank verification',
      'Secure payment processing',
      'Payment status tracking',
      'Multiple payment accounts',
    ],
    isPopular: false,
    defaultSettings: {
      accounts: [],
    } satisfies PaymentsIntegrationSettings,
  },
} as const satisfies Record<IntegrationKey, IntegrationConfig>;

export const INTEGRATION_CATEGORIES = {
  erp: { name: 'ERP Systems', icon: StorefrontIcon },
  inventory: { name: 'Inventory Management', icon: PackageIcon },
  accounting: { name: 'Accounting', icon: LightningIcon },
  shipping: { name: 'Shipping & Logistics', icon: PackageIcon },
  other: { name: 'Other', icon: LightningIcon },
} as const;
export function getIntegrationConfig<T extends IntegrationKey>(
  key: T,
): (typeof INTEGRATIONS_CONFIG)[T] {
  return INTEGRATIONS_CONFIG[key];
}

export function getAllIntegrationConfigs(): IntegrationConfig[] {
  return Object.values(INTEGRATIONS_CONFIG);
}
